import { getCustomizeControlsActions } from "@/modules/agent/states/cusomize-controls";
import { useLoggedInMember } from "@/modules/auth/states";
import { getZaniaTeamMemberRoles, useTeamState } from "@/modules/team/states";
import ZAvatarGradient from "@/shared/components/avatarGradient";
import { LoadingSpinner } from "@/shared/components/custom/spin";
import { ZTableDeprecrated } from "@/shared/components/table-deprecrated/TableDeprecrated";
import { ROUTES } from "@/shared/constants/routes";
import { addNotification } from "@/shared/states/notification";
import parsePromise from "@/shared/utils/parsePromise";
import { Button, Dropdown, DropdownItem, DropdownMenu, DropdownTrigger } from "@nextui-org/react";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";
import { ArrowLeft, Edit, File, Star, TextCursorInput, Trash2 } from "lucide-react";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { deleteControlSet, getCustomControlSets, markControlSetAsDefault, renameControlSet, type ControlSet } from "../../requests";
import BrowseControlSetModal from "../third-party-risk/BrowseControlSetModal";
import CreateControlSetModal from "../third-party-risk/CreateControlSetModal";
import ConfirmationModal from "../third-party-risk/DeleteControlSetModal";
import RenameControlSetModal from "../third-party-risk/RenameControlSetModal";

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(relativeTime);

const ControlSetsList = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [controlSets, setControlSets] = useState<ControlSet[]>([]);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isBrowseModalOpen, setIsBrowseModalOpen] = useState(false);
  const [selectedControlSet, setSelectedControlSet] = useState<ControlSet | null>(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [controlSetToDelete, setControlSetToDelete] = useState<ControlSet | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isMarkDefaultModalOpen, setIsMarkDefaultModalOpen] = useState(false);
  const [controlSetToMarkDefault, setControlSetToMarkDefault] = useState<ControlSet | null>(null);
  const [isMarkingDefault, setIsMarkingDefault] = useState(false);
  const [isRenameModalOpen, setIsRenameModalOpen] = useState(false);
  const [controlSetToRename, setControlSetToRename] = useState<ControlSet | null>(null);
  const [isRenaming, setIsRenaming] = useState(false);
  const [renameErrorMessage, setRenameErrorMessage] = useState("");

  const { team } = useTeamState();
  const member = useLoggedInMember();
  const { isTeamOrgAdmin, isTeamAdmin } = member
    ? getZaniaTeamMemberRoles("", member)
    : { isTeamOrgAdmin: false, isTeamAdmin: false };

  const isAdmin = isTeamOrgAdmin || isTeamAdmin;

  useEffect(() => {
    void fetchControlSets();
  }, []);

  // Add event listener for Create New button from layout
  useEffect(() => {
    const handleCreateControlSetModal = () => {
      setIsCreateModalOpen(true);
    };

    window.addEventListener('openCreateControlSetModal', handleCreateControlSetModal);
    
    return () => {
      window.removeEventListener('openCreateControlSetModal', handleCreateControlSetModal);
    };
  }, []);

  const fetchControlSets = async () => {
    setLoading(true);
    try {
      const [error, response] = await parsePromise(getCustomControlSets());
      if (error) {
        addNotification({
          type: "error",
          title: "Failed to fetch control sets",
          message: error?.message || "Something went wrong. Please try again later!",
        });
        return;
      }

      if (response?.success && response.control_sets?.multi_file_vendor_assessment) {
        setControlSets(response.control_sets.multi_file_vendor_assessment);
      }
    } catch (error) {
      console.error("Error fetching control sets:", error);
      addNotification({
        type: "error",
        title: "Failed to fetch control sets",
        message: "Something went wrong. Please try again later!",
      });
    } finally {
      setLoading(false);
    }
  };

  const getControlCount = (controlSet: ControlSet) => {
    return controlSet.number_of_controls || 0;
  };

  const getMemberName = (memberId: string) => {
    const member = team.find((member) => member.member_id === memberId);
    return member?.name || "Unknown";
  };

  const formatLastUpdated = (dateString: string) => {
    if (dayjs(dateString).isValid()) {
      const parsedDate = dayjs.utc(dateString);
      const localDate = parsedDate.local();
      return localDate.fromNow();
    }
    return "Unknown";
  };

  const handleDeleteClick = (controlSet: ControlSet) => {
    setControlSetToDelete(controlSet);
    setIsDeleteModalOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!controlSetToDelete) return;

    setIsDeleting(true);
    try {
      const [error, response] = await parsePromise(deleteControlSet(controlSetToDelete.id));
      if (error) {
        addNotification({
          type: "error",
          message: "An unexpected error occurred. Please try again later.",
        });
        return;
      }

      if (response?.success) {
        addNotification({
          type: "success",
          message: `Control set "${controlSetToDelete.controls_set_name}" deleted successfully.`,
        });

        await fetchControlSets();
        setIsDeleteModalOpen(false);
        setControlSetToDelete(null);
      }
    } catch (error) {
      console.error("Error deleting control set:", error);
      addNotification({
        type: "error",
        title: "Failed to delete control set",
        message: "Something went wrong. Please try again later!",
      });
    } finally {
      setIsDeleting(false);
    }
  };

  const handleDeleteModalClose = () => {
    if (!isDeleting) {
      setIsDeleteModalOpen(false);
      setControlSetToDelete(null);
    }
  };

  const handleMarkDefaultClick = (controlSet: ControlSet) => {
    setControlSetToMarkDefault(controlSet);
    setIsMarkDefaultModalOpen(true);
  };

  const handleMarkDefaultConfirm = async () => {
    if (!controlSetToMarkDefault) return;

    setIsMarkingDefault(true);
    try {
      const [error, response] = await parsePromise(markControlSetAsDefault(controlSetToMarkDefault.id));
      if (error) {
        addNotification({
          type: "error",
          message: "An unexpected error occurred. Please try again later.",
        });
        return;
      }

      if (response?.success) {
        addNotification({
          type: "success",
          message: `Control set "${controlSetToMarkDefault.controls_set_name}" marked as default successfully.`,
        });

        await fetchControlSets();
        setIsMarkDefaultModalOpen(false);
        setControlSetToMarkDefault(null);
      }
    } catch (error) {
      console.error("Error marking control set as default:", error);
      addNotification({
        type: "error",
        message: "An unexpected error occurred. Please try again later.",
      });
    } finally {
      setIsMarkingDefault(false);
    }
  };

  const handleMarkDefaultModalClose = () => {
    if (!isMarkingDefault) {
      setIsMarkDefaultModalOpen(false);
      setControlSetToMarkDefault(null);
    }
  };

  const handleRenameClick = (controlSet: ControlSet) => {
    setControlSetToRename(controlSet);
    setRenameErrorMessage("");
    setIsRenameModalOpen(true);
  };

  const handleRenameConfirm = async (newName: string) => {
    if (!controlSetToRename) return;

    setIsRenaming(true);
    setRenameErrorMessage("");

    try {
      const [error, response] = await parsePromise(
        renameControlSet(controlSetToRename.id, newName, controlSetToRename.controls_set_name)
      );

      if (error) {
        setRenameErrorMessage(error?.message || "Name already exists. Please choose a different name to proceed.");
        return;
      }

      if (response?.success) {
        addNotification({
          type: "success",
          message: "Control set renamed successfully.",
        });

        await fetchControlSets();
        setIsRenameModalOpen(false);
        setControlSetToRename(null);
        setRenameErrorMessage("");
      }
    } catch (error) {
      console.error("Error renaming control set:", error);
      setRenameErrorMessage("An unexpected error occurred. Please try again later.");
    } finally {
      setIsRenaming(false);
    }
  };

  const handleRenameModalClose = () => {
    if (!isRenaming) {
      setIsRenameModalOpen(false);
      setControlSetToRename(null);
      setRenameErrorMessage("");
    }
  };

  const handleEditControls = (controlSet: ControlSet) => {
    if (controlSet?.id && controlSet?.controls_url) {
      const { setControlSetEditContext } = getCustomizeControlsActions();
      setControlSetEditContext({
        isControlSetEdit: true,
        controlSetId: controlSet.id,
        controlsUrl: controlSet.controls_url,
        controlSetName: controlSet.controls_set_name,
      });

      navigate('/settings/control-sets/edit');
    }
  };

  const handleBackClick = () => {
    navigate(`/${ROUTES.SETTINGS}`);
  };

  return (
    <div className="w-full h-full flex flex-col">
      {/* Header with Figma CSS specifications - Frame 5712 */}
      <div 
        className="flex flex-col items-start gap-4 bg-white border-b border-[#E4E4E7]"
        style={{
          boxSizing: 'border-box',
          padding: '16px',
          width: '100%',
          height: '52px',
          isolation: 'isolate',
          flex: 'none',
          order: 0,
          alignSelf: 'stretch',
          flexGrow: 0
        }}
      >
        {/* Questions Header */}
        <div 
          className="flex flex-row justify-between items-center"
          style={{
            boxSizing: 'border-box',
            padding: '0px',
            gap: '8px',
            isolation: 'isolate',
            width: '100%',
            height: '20px',
            flex: 'none',
            order: 0,
            alignSelf: 'stretch',
            flexGrow: 0,
            zIndex: 1
          }}
        >
          {/* Frame 2767 - Left section with back arrow and title */}
          <div 
            className="flex flex-row items-center"
            style={{
              padding: '0px',
              gap: '8px',
              margin: '0 auto',
              width: '100%',
              height: '20px',
              flex: 'none',
              order: 0,
              flexGrow: 1,
              zIndex: 0
            }}
          >
            {/* Back Arrow */}
            <button
              onClick={handleBackClick}
              className="flex items-center justify-center"
              style={{
                width: '20px',
                height: '20px',
                flex: 'none',
                order: 0,
                flexGrow: 0
              }}
            >
              <ArrowLeft 
                size={20} 
                style={{
                  color: '#000000',
                  strokeWidth: '1.5px'
                }}
              />
            </button>

            {/* Title */}
            <h4
              style={{
                width: '100%',
                height: '20px',
                fontFamily: 'Inter',
                fontStyle: 'normal',
                fontWeight: 500,
                fontSize: '14px',
                lineHeight: '20px',
                color: '#171717',
                flex: 'none',
                order: 1,
                flexGrow: 1
              }}
            >
              Third-Party Risk Assessment Controls
            </h4>
          </div>
        </div>
      </div>

      <div className="grow w-full overflow-hidden flex flex-col">
        <div className="grow overflow-hidden flex">
          <div className="flex-1 flex flex-col bg-white p-6 overflow-hidden">
            <ZTableDeprecrated<ControlSet>
              className="border-1 border-[#E4E4E7] rounded-lg overflow-auto scroll-auto"
              loading={loading}
              rows={controlSets}
              columns={[
                {
                  type: "string",
                  fieldName: "controls_set_name",
                  headerName: "Control Set",
                  sortable: true,
                  renderCell: (row) => {
                    return (
                      <div className="flex items-center gap-3">
                        <File className="h-4 w-4 text-[#717179]" />
                        <div className="flex items-center gap-2">
                          <p className="text-[#333]">{row.controls_set_name}</p>
                          {row.default_control_set && (
                            <span className="px-2 py-1 bg-[#E6F1FE] text-primary text-xs rounded-md font-medium">
                              DEFAULT
                            </span>
                          )}
                        </div>
                      </div>
                    );
                  },
                },
                {
                  type: "number",
                  fieldName: "control_count",
                  headerName: "No. of Controls",
                  sortable: true,
                  renderCell: (row) => {
                    return (
                      <div className="text-[#333]">
                        {getControlCount(row)}
                      </div>
                    );
                  },
                },
                {
                  type: "string",
                  fieldName: "last_updated_by",
                  headerName: "Updated by",
                  sortable: true,
                  renderCell: (row) => {
                    const userName = getMemberName(row.last_updated_by || "");

                    return (
                      <div className="flex items-center gap-2">
                        <ZAvatarGradient
                          name={userName}
                          size={20}
                          textSize={8}
                        />
                        <span className="text-[#333]">{userName}</span>
                      </div>
                    );
                  },
                },
                {
                  type: "string",
                  fieldName: "updated_on",
                  headerName: "Last Updated",
                  sortable: true,
                  renderCell: (row) => {
                    return (
                      <div className="text-[#333] h-10 items-center  flex">
                        {formatLastUpdated(row.last_updated_on)}
                      </div>
                    );
                  },
                },
                {
                  type: "custom",
                  fieldName: "actions",
                  headerName: "",
                  renderCell: (row) => {
                    // Only show actions dropdown for admins
                    if (!isAdmin) {
                      return null;
                    }

                    return (
                      <div className="flex justify-end items-center h-10">
                        <Dropdown placement="bottom-end">
                          <DropdownTrigger>
                            <Button
                              isIconOnly
                              variant="light"
                              className="bg-transparent"
                              disabled={loading}
                            >
                              <svg
                                width="20"
                                height="20"
                                viewBox="0 0 20 20"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                              >
                                <path
                                  d="M10 10.8333C10.4603 10.8333 10.8334 10.4602 10.8334 10C10.8334 9.53976 10.4603 9.16667 10 9.16667C9.53978 9.16667 9.16669 9.53976 9.16669 10C9.16669 10.4602 9.53978 10.8333 10 10.8333Z"
                                  fill="#717179"
                                />
                                <path
                                  d="M10 5.83333C10.4603 5.83333 10.8334 5.46024 10.8334 5C10.8334 4.53976 10.4603 4.16667 10 4.16667C9.53978 4.16667 9.16669 4.53976 9.16669 5C9.16669 5.46024 9.53978 5.83333 10 5.83333Z"
                                  fill="#717179"
                                />
                                <path
                                  d="M10 15.8333C10.4603 15.8333 10.8334 15.4602 10.8334 15C10.8334 14.5398 10.4603 14.1667 10 14.1667C9.53978 14.1667 9.16669 14.5398 9.16669 15C9.16669 15.4602 9.53978 15.8333 10 15.8333Z"
                                  fill="#717179"
                                />
                              </svg>
                            </Button>
                          </DropdownTrigger>
                          <DropdownMenu
                            aria-label="Control Set Actions"
                            variant="flat"
                            disabledKeys={row.default_control_set ? ["delete", "default"] : []}
                            onAction={(key) => {
                              if (key === "edit") {
                                handleEditControls(row);
                              } else if (key === "delete") {
                                handleDeleteClick(row);
                              } else if (key === "default") {
                                handleMarkDefaultClick(row);
                              } else if (key === "rename") {
                                handleRenameClick(row);
                              }
                            }}
                          >
                            <DropdownItem key="edit" startContent={<Edit size={16} />}>
                              Edit Controls
                            </DropdownItem>
                            <DropdownItem key="rename" startContent={<TextCursorInput size={16} />}>
                              Rename
                            </DropdownItem>
                            <DropdownItem key="default" startContent={<Star size={16} />}>
                              Mark as Default Controls
                            </DropdownItem>
                            <DropdownItem key="delete" color="danger" startContent={<Trash2 size={16} />} className="text-danger">
                              Delete
                            </DropdownItem>
                          </DropdownMenu>
                        </Dropdown>
                      </div>
                    );
                  },
                  className: "w-[60px]",
                },
              ]}
              isHeaderSticky={true}
              isCompact={true}
              removeWrapper={true}
              getRowId={(row) => row.id}
              tableHeaderProps={{
                className: "shadow-none rounded-none",
              }}
              tableBodyProps={{
                emptyContent: loading ? (
                  <div className="flex items-center justify-center">
                    <LoadingSpinner
                      containerClassName="bg-transparent relative"
                      className="mx-auto"
                    />
                  </div>
                ) : (
                  "No control sets found"
                ),
              }}
              tableRowProps={(row, rowIndex) => ({
                className: `${rowIndex === 0 ? "border-none" : "border-y"} hover:bg-[#F2F2F2] cursor-pointer`,
                onClick: () => {
                  setSelectedControlSet(row);
                  setIsBrowseModalOpen(true);
                },
              })}
              classNames={{
                wrapper: "shadow-none rounded-none",
              }}
              aria-label="Third-Party Risk Assessment Control Sets"
            />
          </div>
        </div>
      </div>

      <CreateControlSetModal
        isOpen={isCreateModalOpen}
        onClose={() => {
          setIsCreateModalOpen(false);
        }}
      />

      <BrowseControlSetModal
        isOpen={isBrowseModalOpen}
        onClose={() => {
          setIsBrowseModalOpen(false);
          setSelectedControlSet(null);
        }}
        controlSet={selectedControlSet}
      />

      <ConfirmationModal
        isOpen={isDeleteModalOpen}
        onClose={handleDeleteModalClose}
        onConfirm={handleDeleteConfirm}
        controlSet={controlSetToDelete}
        modalType="delete"
        isLoading={isDeleting}
      />

      <ConfirmationModal
        isOpen={isMarkDefaultModalOpen}
        onClose={handleMarkDefaultModalClose}
        onConfirm={handleMarkDefaultConfirm}
        controlSet={controlSetToMarkDefault}
        modalType="markDefault"
        isLoading={isMarkingDefault}
      />

      <RenameControlSetModal
        isOpen={isRenameModalOpen}
        onClose={handleRenameModalClose}
        onRename={handleRenameConfirm}
        controlSet={controlSetToRename}
        isRenaming={isRenaming}
        errorMessage={renameErrorMessage}
      />
    </div>
  );
};

export default ControlSetsList;